// My HR (UI-3C): where each part of employee self-service lives, and the words its states are shown in. Every state
// is the server's own — attendance days, and each request type's native status. Nothing here invents a lifecycle.

import { REQUEST_STATUS, dayStateLabel, describeShift } from "../myHrText";

export const MY_HR_BASE = "/business/hr/me";
export const MY_HR_SECTIONS = [
  { key: "overview", label: "Overview", to: MY_HR_BASE, end: true },
  { key: "schedule", label: "Schedule", to: `${MY_HR_BASE}/schedule` },
  { key: "attendance", label: "Attendance", to: `${MY_HR_BASE}/attendance` },
  { key: "leave", label: "Leave", to: `${MY_HR_BASE}/leave` },
  { key: "overtime", label: "Overtime", to: `${MY_HR_BASE}/overtime` },
  { key: "requests", label: "Requests", to: `${MY_HR_BASE}/requests` },
  { key: "pay", label: "Pay", to: `${MY_HR_BASE}/pay` },
];
export const sectionPath = (key) => MY_HR_SECTIONS.find((section) => section.key === key)?.to || MY_HR_BASE;

// Links written before the sections had their own addresses (`/business/hr/me?tab=leave`) still land in the right place.
const LEGACY_TAB = {
  schedule: "schedule", attendance: "attendance", requests: "requests", leave: "leave", overtime: "overtime",
  pay: "pay", government: "pay", deductions: "pay", payslips: "payslips",
};
export function legacyTabPath(tab) {
  const key = LEGACY_TAB[String(tab || "").toLowerCase()];
  if (!key) return null;
  return key === "payslips" ? "/business/hr/my-payslips" : sectionPath(key);
}

const DAY_CLASS_WORD = {
  REGULAR_HOLIDAY: "Regular holiday",
  SPECIAL_NON_WORKING: "Special non-working day",
  SPECIAL_WORKING: "Special working day",
};

/**
 * The calendar context of a day, read only: "Eid'l Fitr · Regular holiday", "Closed: Stock count", or
 * "EDSA People Power Anniversary · Special working day". It is information beside the schedule, never instead of
 * it: a special working day is an ordinary working day, and a worked holiday still shows its attendance.
 */
export function calendarLine(day) {
  const official = Array.isArray(day?.officialDays) && day.officialDays.length
    ? day.officialDays
    : day?.holiday ? [{ name: day.holiday.name, dayClass: null }] : [];
  const parts = official.map((item) => `${item.name} · ${DAY_CLASS_WORD[item.dayClass] || "Holiday"}`);
  if (day?.closure?.name) parts.push(`Closed: ${day.closure.name}`);
  return parts.join("; ");
}

/** What a day's schedule says, in one line. */
export function scheduleLine(day) {
  if (day?.leave) return `On leave${day.leave.typeName ? ` (${day.leave.typeName})` : ""}`;
  const schedule = day?.schedule || day;
  if (!schedule) return "No schedule";
  if (schedule.kind === "work" && schedule.shift) return describeShift(schedule.shift);
  if (schedule.kind === "rest") return "Rest day";
  if (schedule.kind === "conflict") return "Schedule problem — HR has to fix it";
  return "No schedule";
}

// The schedule and calendar lines already say "Rest day", "No schedule", the holiday, the closure or "On leave"; the
// state is not repeated.
export function showsDayState(day) {
  return day?.correction?.status === "pending" || !["rest", "no_schedule", "holiday", "closed", "on_leave"].includes(day?.state);
}

/** A day's attendance state as a word and a tone for `Status` (colour only where a person has to act). */
export function dayState(day) {
  const { label, tone } = dayStateLabel(day);
  return { label, tone: tone === "warn" ? "warning" : "neutral" };
}

const REQUEST_TONE = { approved: "success", rejected: "danger" };

/** A request's own status in words. An assigned overtime that was withdrawn says so. */
export function requestState(request) {
  if (request?.status === "cancelled" && request?.origin === "assigned") return { label: "Withdrawn", tone: "neutral" };
  const status = REQUEST_STATUS[request?.status] || REQUEST_STATUS.pending;
  return { label: status.label, tone: REQUEST_TONE[request?.status] || "neutral" };
}

/** Who decided a request and what they wrote, or null while it waits. Field names differ by request type. */
export function decisionLine(request) {
  const status = request?.status;
  if (status !== "approved" && status !== "rejected") return null;
  const reviewer = request.reviewerName || request.reviewer_name;
  const note = request.reviewNote || request.review_notes;
  const owner = request.ownerOverride || request.owner_override;
  return `${REQUEST_STATUS[status].label} by ${reviewer || "a reviewer"}${note ? `: “${note}”` : ""}${owner ? " (owner decision)" : ""}`;
}

/** An employee may withdraw their own request while it waits; overtime a manager assigned is not theirs to cancel. */
export function canCancel(request) {
  return request?.status === "pending" && !(request.type === "overtime" && request.origin === "assigned");
}
